import { getIdToken } from './firebase/auth';
import {
    AccessRequest,
    AccessRole,
    ClaimsUpdateBody,
    CreateGroupWithUserBody,
    CreateInterviewBody,
    Group,
    GroupClaim,
    Interview,
    RequestAccessBody,
    UserCreationForm,
    UserWithClaims,
    getIdFromName,
} from '@synergetx/puppet-interview-types';
import { breadcrumb, log } from './logging';
import { API_BASE_URL } from './consts';
import { stringFromError } from './firebase/firestore-utils';
import { ApiError } from './api-error';

export { ApiError };

const groupUrl = (groupId: string) => `${API_BASE_URL}/group/${groupId}`;

export async function getCsvDownloadUrl(groupId: string, interviewId: string): Promise<string> {
    const { url } = await postToApi<{ url: string }>(
        `${groupUrl(groupId)}/csv/${interviewId}`,
        'getCsvDownloadUrl',
    );

    if (!url) {
        throw new ApiError('The server did not return a download link. Please try again.', {
            path: 'getCsvDownloadUrl',
        });
    }

    return url;
}

export async function getAllUsersWithClaimsInGroupInterview(
    groupId: string,
): Promise<UserWithClaims[]> {
    const users = await postToApi<UserWithClaims[]>(
        `${groupUrl(groupId)}/users`,
        'getAllUsersWithClaimsInGroupInterview',
    );

    return users ?? [];
}

export async function setClaims(groupId: string, role: AccessRole, userId: string): Promise<void> {
    const body: ClaimsUpdateBody = { role, groupId };

    await postToApi(`${groupUrl(groupId)}/user/${userId}/claims`, 'setClaims', body);
}

export async function createNewUserForInterview(
    groupId: string,
    newClaims: GroupClaim,
    userInfo: UserCreationForm,
) {
    return postToApi<{ message: string; user: UserWithClaims }>(
        `${groupUrl(groupId)}/user/createUser`,
        'createNewUserForInterview',
        { newClaims, newUserInfo: userInfo },
    );
}

export async function createNewGroup(formValues: CreateInterviewBody) {
    breadcrumb(`creating group`, 'API', { groupId: formValues.groupId });

    const createdInterview = await postToApi<Interview>(`${API_BASE_URL}/group`, 'createNewGroup', {
        ...formValues,
    });

    // The name is taken now, so don't let a cached "available" survive this session.
    groupExistsCache.set(formValues.groupId, true);

    // update user's claims, give the auth system a chance to update local user claims
    await getIdToken();

    return createdInterview;
}

export async function createGroupAndUserAdmin(info: CreateGroupWithUserBody) {
    const { assignedGroupId } = info.accessRequest;

    if (!assignedGroupId) {
        throw new ApiError('This request has no group assigned to it yet.', {
            path: 'createGroupAndUserAdmin',
        });
    }

    breadcrumb(`creating group and user admin`, 'API', { assignedGroupId });

    const createdGroupAndUser = await postToApi<Pick<CreateGroupWithUserBody, 'accessRequest'>>(
        `${groupUrl(assignedGroupId)}/createGroupAndAdmin`,
        'createGroupAndUserAdmin',
        info,
    );

    // The name is taken now, so don't let a cached "available" survive this session.
    groupExistsCache.set(assignedGroupId, true);

    // update user's claims, give the auth system a chance to update local user claims
    await getIdToken();

    return createdGroupAndUser;
}

export const updateAccessPin = async (
    groupId: string,
    accessPIN: CreateInterviewBody['accessPIN'],
) =>
    postToApi<{ message: string; group: Group }>(
        `${groupUrl(groupId)}/access-pin`,
        'updateAccessPin',
        { accessPIN },
    );

export async function createNewInterview(values: CreateInterviewBody) {
    breadcrumb(`creating interview`, 'API', { groupId: values.groupId, name: values.name });

    const created = await postToApi<{ message: string; interview: Interview }>(
        `${groupUrl(values.groupId)}/interview`,
        'createNewInterview',
        { ...values },
    );

    // update user's claims, give the auth system a chance to update local user claims
    await getIdToken();

    return created;
}

export async function initializeUser(userId: string): Promise<void> {
    await postToApi(`${API_BASE_URL}/user/${userId}/initialize`, 'initializeUser');
}

export async function submitAccessRequest(values: RequestAccessBody) {
    return postToApi<RequestAccessBody>(`${API_BASE_URL}/request-access`, 'submitAccessRequest', {
        ...values,
    });
}

export async function updateAccessRequestStatus(values: Partial<AccessRequest>) {
    breadcrumb(`update access status`, 'API', { id: values.id, status: values.status });

    return postToApi<AccessRequest>(
        `${API_BASE_URL}/update-access-status`,
        'updateAccessRequestStatus',
        { ...values },
    );
}

// --- group name availability ------------------------------------------------------------

const GROUP_NAME_CHECK_DEBOUNCE_MS = 400;

/** Answers already paid for, keyed by derived group id. Group ids are never recycled. */
const groupExistsCache = new Map<string, boolean>();

/** The keystroke currently waiting out the debounce window, if any. */
let pendingGroupCheck: { timer: ReturnType<typeof setTimeout>; resolve: () => void } | null = null;

async function groupExists(groupId: string): Promise<boolean> {
    // NOTE: don't use the unified group url builder, this group isn't the currently selected one
    try {
        const response = await postToApi<{ groupExists: boolean }>(
            `${API_BASE_URL}/group/${groupId}/groupExists`,
            'groupExists',
        );

        return !!response?.groupExists;
    } catch (error) {
        // Fail open: the backend rejects a duplicate on create, so a flaky availability check
        // should not be what stops someone from naming a group.
        log(`[groupExists] check failed, treating "${groupId}" as available`, {
            groupId,
            error: stringFromError(error),
        });

        return false;
    }
}

/**
 * Resolves true when no group has claimed `name` yet. Used as a Yup test, so each caller gets a
 * promise bound to the name it asked about — never reuse `lodash/debounce` here, which returns
 * the *previous* call's value and failed every new name with "Group already exists".
 */
export function isNewGroup(name: string | undefined): Promise<boolean> {
    // `required()` owns the empty case; don't spend a request on it.
    if (!name) return Promise.resolve(true);

    const groupId = getIdFromName(name);
    const cached = groupExistsCache.get(groupId);
    if (cached !== undefined) return Promise.resolve(!cached);

    // A newer keystroke wins, so resolve the superseded check as valid rather than flashing an error.
    if (pendingGroupCheck) {
        clearTimeout(pendingGroupCheck.timer);
        pendingGroupCheck.resolve();
        pendingGroupCheck = null;
    }

    return new Promise<boolean>((resolve) => {
        const timer = setTimeout(() => {
            pendingGroupCheck = null;
            void groupExists(groupId).then((exists) => {
                groupExistsCache.set(groupId, exists);
                resolve(!exists);
            });
        }, GROUP_NAME_CHECK_DEBOUNCE_MS);

        pendingGroupCheck = { timer, resolve: () => resolve(true) };
    });
}

// --- transport --------------------------------------------------------------------------

const JSON_POST_HEADER = {
    method: 'POST',
    headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
    },
    mode: 'cors',
} satisfies RequestInit;

/** What we tell the user, for the statuses where the raw server text wouldn't help them. */
const STATUS_MESSAGES: Record<number, string> = {
    401: 'Your session has expired. Please sign in again.',
    403: 'You do not have permission to do that.',
    404: 'We could not find that on the server.',
    429: 'Too many requests. Please wait a moment and try again.',
    500: 'The server ran into a problem. Please try again.',
    503: 'The server is temporarily unavailable. Please try again shortly.',
};

/** Pulls a human-readable message out of an error response body, if there is one. */
async function messageFromResponse(response: Response): Promise<string | undefined> {
    try {
        const text = await response.text();
        if (!text) return undefined;

        try {
            const parsed = JSON.parse(text) as { message?: string; error?: string };
            return parsed.message || parsed.error || undefined;
        } catch {
            // Not JSON — a proxy or gateway error page. Don't show HTML to the user.
            return text.trimStart().startsWith('<') ? undefined : text.slice(0, 200);
        }
    } catch {
        return undefined;
    }
}

/**
 * POSTs to the Cloud Functions API, resolving the parsed body or throwing {@link ApiError}. The
 * ID token goes in the body (what the backend expects), attached after the breadcrumb so it
 * never reaches Sentry.
 */
async function postToApi<T>(path: string, description: string, body: object = {}): Promise<T> {
    const firebaseIdToken = await getIdToken();

    breadcrumb(`posting: ${description}`, 'API', { path });

    let response: Response;
    try {
        response = await fetch(path, {
            ...JSON_POST_HEADER,
            body: JSON.stringify({
                firebaseIdToken, // insert token so API can auth the user
                ...body,
            }),
        });
    } catch (error) {
        log(`[postToApi] ${description}: request failed`, {
            path,
            error: stringFromError(error),
        });

        throw new ApiError('Could not reach the server. Check your connection and try again.', {
            path,
            cause: error,
        });
    }

    breadcrumb(`PostToApi ${description} Response`, 'API', {
        responseStatus: response.status,
        responseOk: response.ok,
        responseStatusText: response.statusText,
    });

    if (!response.ok) {
        const serverMessage = await messageFromResponse(response);
        const message =
            STATUS_MESSAGES[response.status] ??
            serverMessage ??
            `The server returned an error (${response.status}).`;

        log(`[postToApi] ${description}: ${response.status} ${response.statusText}`, {
            path,
            status: response.status,
            serverMessage,
        });

        throw new ApiError(message, { path, status: response.status });
    }

    try {
        return (await response.json()) as T;
    } catch (error) {
        log(`[postToApi] ${description}: unreadable response body`, {
            path,
            error: stringFromError(error),
        });

        throw new ApiError('The server sent back a response we could not read.', {
            path,
            status: response.status,
            cause: error,
        });
    }
}
